import { Injectable } from '@angular/core';
import { BcmSettingsApiService } from '@modules/bcm/@shared/services';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { finalize, map, tap } from 'rxjs/operators';
import { AppNotificationService } from '@core/services/app-notification.service';
import { TranslationService } from '@core/translation/translation.service';
import { BcmUserSettingsStateService } from '@bcmServices/settings/bcm-user-settings-state.service';
import {
    BcmUserSettingKey,
    BcmUserSettings,
    BcmUserSettingsSection,
    BcmUserTableSettingKey
} from '@shared/models/bcm-settings-user';

@Injectable({providedIn: 'root'})
export class BcmUserSettingsFacade {

    get isUpdating$(): Observable<boolean> {
        return this.state.isUpdating$;
    }

    get settings$(): Observable<BcmUserSettings> {
        return this.state.getSettings$();
    }

    getCurrentSettings(): BcmUserSettings {
        return this.state.getCurrentSettings();
    }

    constructor(private api: BcmSettingsApiService,
                private state: BcmUserSettingsStateService,
                private appNotificationService: AppNotificationService,
    ) {
    }

    loadSettings(): Observable<BcmUserSettings> {
        return this.api.getUserSettings()
            .pipe(
                // The API responds with an empty body when the user has no settings
                // rows in this tenant, which arrives here as null.
                map(settings => settings || {} as BcmUserSettings),
                tap(settings => this.state.setSettings(settings)),
            );
    }

    updateSingleSetting<T extends BcmUserSettingsSection>(
        key: BcmUserSettingKey | BcmUserTableSettingKey | string,
        settings: T,
        preventMessage = false,
    ): Subscription {
        this.state.startUpdating();

        return this.api.updateSingleUserSetting<T>(key, settings)
            .subscribe({
                next: () => {
                    if (!preventMessage) {
                        this.appNotificationService.showSuccess('Einstellung(en) erfolgreich gespeichert');
                    }
                    this.state.updateSingleSetting<T>(key, settings);
                },
                error: (error) => this.handleError(error),
                complete: () => this.state.stopUpdating()
            });
    }

    applySettingsLocally(
        items: {
            key: (BcmUserSettingKey | BcmUserTableSettingKey | string),
            settings: BcmUserSettingsSection
        }[],
        preventMessage = false,
    ): Observable<void> {
        this.state.startUpdating();

        return of(undefined).pipe(
            tap(() => {
                if (!preventMessage) {
                    this.appNotificationService.showSuccess(TranslationService.translate('common.notify.changesSaved'));
                }
                for (const item of items) {
                    this.state.updateSingleSetting(item.key, item.settings);
                }
            }),
            map(() => undefined),
            finalize(() => this.state.stopUpdating()),
        );
    }

    updateMultipleSettings(
        items: {
            key: (BcmUserSettingKey | BcmUserTableSettingKey | string),
            settings: BcmUserSettingsSection
        }[],
        preventMessage = false,
    ): Observable<void> {
        this.state.startUpdating();
        return forkJoin(items.map(item => this.api.updateSingleUserSetting(item.key, item.settings)))
            .pipe(
                tap({
                    next: () => {
                        if (!preventMessage) {
                            this.appNotificationService.showSuccess('Einstellung(en) erfolgreich gespeichert');
                        }
                        for (const item of items) {
                            this.state.updateSingleSetting(item.key, item.settings);
                        }
                    },
                    error: (error) => this.handleError(error),
                }),
                map(() => undefined),
                // `finalize` instead of a `complete` handler: on error the updating flag used to stay
                // set, leaving the settings dialog's save buttons disabled for good. (#6951)
                finalize(() => this.state.stopUpdating()),
            );
    }

    private handleError(error: Error): void {
        // todo: handle settings specific errors and propagate rest with throw
        throw error;
    }
}
